"use client";

import { enUS, fi } from "date-fns/locale";
import { formatInTimeZone } from "date-fns-tz";
import { useLocale, useTranslations } from "next-intl";
import { CountdownCard } from "./CountdownCard";

interface TournamentCountdownProps {
  towerOpensAt: Date;
  tournamentStartsAt: Date;
  signInStartsAt: Date | null;
  signInEndsAt: Date | null;
  isSignInOpen: boolean;
  signInNotYetOpen: boolean;
  // When joining is allowed during the active tournament, sign-in has no hard
  // deadline — registration effectively stays open — so the "registration ends"
  // countdown and the "registration closed" card would be misleading and are hidden.
  allowJoinDuringTournament: boolean;
}

export function TournamentCountdown({
  towerOpensAt,
  tournamentStartsAt,
  signInStartsAt,
  signInEndsAt,
  isSignInOpen,
  signInNotYetOpen,
  allowJoinDuringTournament,
}: TournamentCountdownProps) {
  const t = useTranslations();
  const locale = useLocale();
  const dateLocale = locale === "fi" ? fi : enUS;
  const atWord = locale === "fi" ? "klo" : "at";

  return (
    <div className="w-full mb-8">
      <div className="grid gap-4 sm:grid-cols-3">
        {/* 1. Sign-in not yet open - countdown to opening */}
        {signInNotYetOpen && signInStartsAt && (
          <CountdownCard
            targetDate={signInStartsAt}
            icon="✍️"
            title={t("tournaments.countdown.signInOpens")}
            accent="sky"
          />
        )}

        {/* 1. Sign-in Ends Card — hidden when joining stays open during the
            active tournament, since there is no real registration deadline. */}
        {!allowJoinDuringTournament && isSignInOpen && signInEndsAt && (
          <CountdownCard
            targetDate={signInEndsAt}
            icon="✍️"
            title={t("tournaments.countdown.signInEnds")}
            accent="emerald"
          />
        )}

        {/* 1. Sign-in closed state */}
        {!allowJoinDuringTournament &&
          !isSignInOpen &&
          !signInNotYetOpen &&
          signInEndsAt && (
          <div className="relative overflow-hidden rounded-2xl border border-default-200/50 bg-gradient-to-br from-default-100/50 to-default-200/30 p-6">
            <div className="relative flex flex-col items-center text-center">
              {/* Icon */}
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-default-200">
                <span className="text-2xl opacity-50">✍️</span>
              </div>

              {/* Title */}
              <h3 className="mb-1 font-cinzel text-sm font-semibold uppercase tracking-wider text-default-500">
                {t("tournaments.countdown.signInClosed")}
              </h3>

              {/* Date */}
              <p className="font-spectral text-lg text-default-400">
                {formatInTimeZone(
                  signInEndsAt,
                  "Europe/Helsinki",
                  `d. MMMM yyyy '${atWord}' HH:mm`,
                  { locale: dateLocale },
                )}
              </p>
            </div>
          </div>
        )}

        {/* 2. Tower Opens Card */}
        <CountdownCard
          targetDate={towerOpensAt}
          icon="🏰"
          title={t("tournaments.countdown.towerOpens")}
          accent="amber"
        />

        {/* 3. Tournament Starts Card */}
        <CountdownCard
          targetDate={tournamentStartsAt}
          icon="🏆"
          title={t("tournaments.countdown.tournamentStarts")}
          accent="amber"
        />
      </div>
    </div>
  );
}
